import PropTypes from 'prop-types'
import React from 'react'
import { findDOMNode } from 'react-dom'
import { injectIntl, FormattedMessage } from 'react-intl'
import { SubmissionError } from 'redux-form'
import { CHECKLIST_ITEM_NAME_MAX_LENGTH } from 'const/limits'
import { PermissionLevels } from 'utils/permission'
import {
  ProgressBar,
  Button,
  ButtonGroup
} from 'react-bootstrap'
import {
  ContentBody,
  ContentButton,
  ContentIcon,
  ContentTitle
} from 'components/Content'
import {
  MenuItem
} from 'components/Menu'
import {
  ToggleModal
} from 'components/Modal'
import KanBoPicker from 'components/KanBoPicker'
import getCharCount from 'components/RichText/utils/getCharCount'
import trimRichText from 'components/RichText/utils/trim'
import KanBoIcon from 'components/KanBoIcon'
import Sortable from 'components/Sortable'
import Element from '../Element'
import ElementInfo from '../ElementInfo'
import TemplateField from 'components/CardTemplateField'
import AddChecklistItem from './AddChecklistItem'
import ChecklistItem from './ChecklistItem'
import checklistItemDragType from './ChecklistItem/dragType'
import sweetalert from 'sweetalert2'
import classes from './Checklist.module.scss'
import { withBoardPermission } from 'dataProviders/Board'

export class Checklist extends React.Component {
  static propTypes = {
    cardContext: PropTypes.object.isRequired,

    intl: PropTypes.object.isRequired,

    permission: PropTypes.object.isRequired,

    isCardArchived: PropTypes.bool.isRequired,
    boardId: PropTypes.number.isRequired,
    statusId: PropTypes.number.isRequired,
    items: PropTypes.array.isRequired,
    itemsCount: PropTypes.number.isRequired,
    completedItemsCount: PropTypes.number.isRequired,
    isBusy: PropTypes.bool,
    isNewlyAdded: PropTypes.bool,
    isTemplate: PropTypes.bool.isRequired,

    requestAddChecklistItem: PropTypes.func.isRequired,
    requestAddChecklistItemArray: PropTypes.func.isRequired,
    requestPositionChecklistItem: PropTypes.func.isRequired,
    requestConvertChecklistToSubCardGroup: PropTypes.func.isRequired,
    setChecklist: PropTypes.func.isRequired,

    id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ]),
    sortable: PropTypes.object.isRequired
  }

  state = {
    disabledOnClickOutside: false,
    showAddItem: false,
    isConverting: false,
    convertBoardId: this.props.boardId,
    convertStatusId: null,
    convertTemplate: null,
  }

  componentDidMount = () => {
    const { cardContext, id, setChecklist } = this.props

    if (
      this.props.isNewlyAdded
    ) {
      this.handleAddItemClick()
      setChecklist({
        cardId: cardContext.id,
        element: {
          id,
          isNewlyAdded: false
        }
      })
    }
  }

  componentDidUpdate = (prevProps) => {
    const { cardContext, id, setChecklist } = this.props

    if (
      this.props.isNewlyAdded &&
      !prevProps.isNewlyAdded
    ) {
      this.handleAddItemClick()
      setChecklist({
        cardId: cardContext.id,
        element: {
          id,
          isNewlyAdded: false
        }
      })
    }
  }

  handleAddItemClick = () => {
    this.setState({
      showAddItem: !this.state.showAddItem
    })
  }

  handleAddItemClose = () => {
    this.setState({
      showAddItem: false
    })
  }

  handleChecklistItemPositionChange = ({ itemId, parentId, position }) => {
    this.props.requestPositionChecklistItem({
      cardId: this.props.cardContext.id,
      checklistItemId: itemId,
      checklistId: parentId,
      position
    })
  }

  handleSubmit = ({ name }) => {
    const {
      cardContext,
      intl,
      id,
      requestAddChecklistItem,
      requestAddChecklistItemArray
    } = this.props

    return new Promise((resolve) => {
      const contentCharCount = getCharCount(name)
      if (contentCharCount > CHECKLIST_ITEM_NAME_MAX_LENGTH) {
        throw new SubmissionError({
          name: intl.formatMessage({ id: 'CHECKLISTITEM_NAME_TOO_LONG' },
            { limit: CHECKLIST_ITEM_NAME_MAX_LENGTH })
        })
      }

      if (contentCharCount > 0) {
        if (name.Blocks.length >= 2) {
          const { Blocks, ...RichText } = name
          const possibleTaskBlocks = Blocks.filter(Block => Block.Text && Block.Text.length)

          if (possibleTaskBlocks.length >= 2) {
            setTimeout(() => {
              this.setState({
                disabledOnClickOutside: true
              }, () => {
                sweetalert({
                  text: intl.formatMessage({ id: 'CHECKLISTITEM_ADD_POPULATE_QUESTION' }),
                  confirmButtonText: intl.formatMessage({ id: 'YES' }),
                  cancelButtonText: intl.formatMessage({ id: 'NO' }),
                  showCancelButton: true,
                  type: 'question'
                }).then(() => {
                  requestAddChecklistItemArray({
                    cardId: cardContext.id,
                    checklistId: id,
                    items: possibleTaskBlocks.map(TaskBlock => ({
                      name: trimRichText({
                        ...RichText,
                        Blocks: [TaskBlock]
                      })
                    }))
                  })

                  this.setState({
                    disabledOnClickOutside: false
                  }, () => { resolve() })
                }, () => {
                  requestAddChecklistItem({
                    cardId: cardContext.id,
                    checklistId: id,
                    name: trimRichText(name)
                  })

                  this.setState({
                    disabledOnClickOutside: false
                  }, () => { resolve() })
                })
              })
            }, 10)

            return
          }
        }

        requestAddChecklistItem({
          cardId: cardContext.id,
          checklistId: id,
          name: trimRichText(name)
        })
      }

      resolve()
    })
  }

  hideModalToggle = () => {
    this.setState({
      isConverting: false,
      convertBoardId: this.props.boardId,
      convertStatusId: null,
      convertTemplate: null,
    })
  }

  handleConvertToSubCardGroupClick = () => {
    this.setState({
      isConverting: true,
      convertBoardId: this.props.boardId,
      convertStatusId: this.props.statusId,
      convertTemplate: null,
    })
  }

  handleBoardChange = (convertBoardId) => {
    this.setState({
      convertBoardId,
      convertTemplate: null,
    })
  }

  handleStatusChange = (convertStatusId) => {
    this.setState({
      convertStatusId
    })
  }

  handleTemplateChange = (template) => {
    this.setState({
      convertTemplate: template
    })
  }

  handleConvertClick = () => {
    const { cardContext, id, requestConvertChecklistToSubCardGroup } = this.props
    const { convertStatusId, convertTemplate } = this.state

    requestConvertChecklistToSubCardGroup({
      cardId: cardContext.id,
      checklistId: id,
      statusId: convertStatusId,
      template: (convertTemplate && convertTemplate !== 'empty') ? { type: 'card', id: +convertTemplate } : 'empty',
    })
  }

  renderItem = React.forwardRef((props, ref) => {
    const { id } = this.props

    return (
      <ChecklistItem
        {...props}
        ref={ref}
        checklistId={id}
      />
    )
  })

  render () {
    const {
      permission,
      isCardArchived,
      boardId,
      statusId,
      items,
      itemsCount,
      completedItemsCount,
      isBusy,
      isTemplate,

      id,
      sortable
    } = this.props
    const {
      disabledOnClickOutside,
      showAddItem,
      isConverting,
      convertBoardId,
      convertStatusId,
      convertTemplate
    } = this.state

    let completedPercentage = 0
    if (itemsCount > 0 && completedItemsCount > 0) {
      completedPercentage = Math.floor(completedItemsCount / itemsCount * 100)
    }

    let progressBarBsStyle = null
    if (completedPercentage === 100) {
      progressBarBsStyle = 'success'
    }

    return (
      <Sortable
        vertical
        containerTag='ul'
        className={classes.contentChild + ' list-group'}
        name='checklistItems'
        id={id}
        component={this.renderItem}
        dragType={checklistItemDragType}
        items={items}
        disabled={isBusy || isCardArchived || !permission.member}
        onPositionChange={this.handleChecklistItemPositionChange}
      >
        {({ renderContainer, connectDropTarget }) =>
          <ToggleModal
            show={isConverting}
            backdrop
            attachClickHandler={false}
            attachment='top right'
            targetAttachment='top right'
            constraints={[
              {
                to: 'window',
                pin: true
              }
            ]}
            onToggle={this.hideModalToggle}
          >
            <Element
              ref={ref => {
                connectDropTarget(!isBusy ? findDOMNode(ref) : null)
              }}
              id={id}
              icon={<KanBoIcon name='kanbo-24-task-list' />}
              collapsible
              sortable={sortable}
              menu={
                (!isTemplate && itemsCount > 0)
                  ? (
                    <MenuItem
                      icon={<KanBoIcon name='kanbo-16-relation-child' />}
                      onClick={this.handleConvertToSubCardGroupClick}
                    >
                      <FormattedMessage id='CHECKLIST_CONVERT_TO_SUBCARD_GROUP' />
                    </MenuItem>
                  )
                  : undefined
              }
            >
              {(collapsed) =>
                <React.Fragment>
                  {(!collapsed || items.length > 0) &&
                    <ContentBody
                      className={classes.body}
                      style={!collapsed ? { paddingBottom: 0 } : null}
                    >
                      {items.length > 0 &&
                        <ProgressBar
                          style={{
                            height: 10,
                            margin: '0 10px'
                          }}
                          className={classes.contentChild}
                          now={completedPercentage}
                          bsStyle={progressBarBsStyle}
                        />
                      }
                      {!collapsed && renderContainer()}
                    </ContentBody>
                  }
                  {collapsed && (
                    <ContentBody>
                      <ElementInfo
                        icon={<KanBoIcon name='kanbo-16-tasks' />}
                      >
                        {completedItemsCount}{'/'}{itemsCount}
                      </ElementInfo>
                    </ContentBody>
                  )}
                  {(!collapsed && !isCardArchived && permission.member && showAddItem) && (
                    <ContentBody
                      style={{ paddingTop: 0 }}
                    >
                      <AddChecklistItem
                        id={id}
                        boardId={boardId}
                        className={classes.contentChild}
                        onSubmit={this.handleSubmit}
                        onClose={this.handleAddItemClose}
                        disableOnClickOutside={disabledOnClickOutside}
                        disabledOnClickOutside={disabledOnClickOutside}
                      />
                    </ContentBody>
                  )}
                  {(!collapsed && !isCardArchived && permission.member && !showAddItem) && (
                    <ContentButton
                      onClick={this.handleAddItemClick}
                    >
                      <ContentIcon>
                        <KanBoIcon name='kanbo-16-plus' />
                      </ContentIcon>
                      <ContentTitle>
                        <FormattedMessage id='CHECKLISTITEM_ADD' />
                      </ContentTitle>
                    </ContentButton>
                  )}
                </React.Fragment>
              }
            </Element>
            <div className={classes.modal}>
              <KanBoPicker
                board
                status
                currentBoardId={boardId}
                currentStatusId={statusId}
                requiredPermissionLevel={PermissionLevels.MEMBER}
                onBoardChange={this.handleBoardChange}
                onStatusChange={this.handleStatusChange}
              />
              <TemplateField
                boardId={convertBoardId}
                value={convertTemplate}
                onChange={this.handleTemplateChange}
              />
              <ButtonGroup>
                <Button
                  bsStyle='primary'
                  disabled={!convertStatusId}
                  onClick={this.handleConvertClick}
                >
                  <FormattedMessage id='CHECKLIST_CONVERT_TO_SUBCARD_GROUP' />
                </Button>
                <Button
                  onClick={this.hideModalToggle}
                >
                  <FormattedMessage id='CANCEL' />
                </Button>
              </ButtonGroup>
            </div>
          </ToggleModal>
        }
      </Sortable>
    )
  }
}

export default injectIntl(withBoardPermission(Checklist), { forwardRef: true })
